<script lang="ts">
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { addDollarSign, getNumBackInUnits, num } from '$lib/util/util';
	import { quadInOut } from 'svelte/easing';
	import { scale } from 'svelte/transition';
	import BigNumber from 'bignumber.js';
	import { onMount } from 'svelte';

	import {
		isStopLossInputValid,
		isTakeProfitInputValid,
		loadingTPSL,
		positionData,
		tpslFees
	} from '../popupsStore';
	import { getTokenLogo } from '$lib/perps';
	import { getDisplayAsset } from '$lib/home/tradingView/util';
	import { marketPrices } from '$lib/util/marketHelper';
	import { marketsConfig } from '$lib/config/markets';
	import { getPostTradeDetailsForTPSL, submitTPSL } from './tpsl';
	import ProcessingButton from '$lib/util/ProcessingButton.svelte';
	import DisabledButton from '$lib/util/DisabledButton.svelte';

	import TakeProfit from './TakeProfit.svelte';
	import StopLoss from './StopLoss.svelte';
	import { margin, userMarginList } from '$lib/home/userInteract/store';

	onMount(() => {
		getPostTradeDetailsForTPSL();
	});

	let positionMargin = '';
	$: {
		let margin = $userMarginList.find(
			(margin) => margin.marketIdentifier === $positionData.marketIdentifier
		)?.depositedMargin;

		if (margin) {
			positionMargin = margin;
		} else {
			positionMargin = $positionData.margin
				? '$' + getNumBackInUnits($positionData.margin, 18, 2)
				: '-';
		}
	}
</script>

<GradientBorder
	gradient="sm:bg-fx-gradient-6"
	paddingClass="p-px"
	borderRadius="rounded-2xl squircle-2xl"
	outerClassList="w-full sm:w-fit h-fit"
	innerClassList="bg-fx-black"
>
	<div
		class="bg-fx-gradient-8-50 text-fx-white p-4 sm:p-8 rounded-2xl w-full sm:w-[400px] sm:min-w-[400px]"
	>
		<div class="text-fx-gradient-5 text-xl font-semibold">Entire Position TP/SL</div>
		<div class="border-t border-t-fx-grey-800 w-full mb-4 mt-2" />
		<GradientBorder
			gradient="bg-fx-gradient-23-50"
			paddingClass="p-px"
			borderRadius="rounded-xl squircle-xl"
			outerClassList="w-full"
			innerClassList=""
		>
			<div class="p-3 w-full bg-fx-gradient-8 rounded-xl">
				<div class="flex justify-between">
					<div class="flex items-center">
						<img
							loading="lazy"
							src={getTokenLogo($positionData.marketIdentifier)}
							alt="logo"
							class="w-5 h-5 mr-2 rounded-full"
						/>
						<div class="text-fx-grey-100 text-sm font-medium text-center mr-2">
							{getNumBackInUnits($positionData.size, 18, 4)}
							{getDisplayAsset($positionData.asset)}
						</div>
						<div class="text-xs text-fx-grey-400">
							(${num(
								getNumBackInUnits(
									new BigNumber($positionData.size)
										.multipliedBy($marketPrices[$positionData.marketIdentifier])
										.div(1e18)
										.abs()
										.toString(),
									18,
									2
								)
							)})
						</div>
					</div>

					<div>
						{#if new BigNumber($positionData.size).gt(0)}
							<div
								class="bg-fx-gradient-8 px-2 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg squircle-lg border border-fx-grey-500"
							>
								Long
							</div>
						{:else}
							<div
								class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg squircle-lg border border-fx-grey-500"
							>
								Short
							</div>
						{/if}
					</div>
				</div>

				<div class="flex justify-between mt-2 items-center">
					<div class="text-sm text-fx-grey-400">Liquidation Price:</div>
					<div class="text-sm text-fx-orange">
						{addDollarSign(
							getNumBackInUnits(
								$positionData.liquidationPrice,
								18,
								marketsConfig[$positionData.asset].showDecimals
							),
							marketsConfig[$positionData.asset].showDecimals
						)}
					</div>
				</div>
				<div class="flex justify-between items-center">
					<div class="text-sm text-fx-grey-400">Index Price:</div>
					<div class="text-sm text-fx-grey-300">
						{addDollarSign(
							getNumBackInUnits(
								$marketPrices[$positionData.marketIdentifier],
								18,
								marketsConfig[$positionData.asset].showDecimals
							),
							marketsConfig[$positionData.asset].showDecimals
						)}
					</div>
				</div>
				<div class="flex justify-between items-center">
					<div class="text-sm text-fx-grey-400">Avg. Entry Price:</div>
					<div class="text-sm text-fx-grey-300">
						{addDollarSign(
							getNumBackInUnits(
								$positionData.avgEntryPrice,
								18,
								marketsConfig[$positionData.asset].showDecimals
							),
							marketsConfig[$positionData.asset].showDecimals
						)}
					</div>
				</div>
				<div class="flex justify-between items-center">
					<div class="text-sm text-fx-grey-400">Margin:</div>
					<div class="text-sm text-fx-grey-300">
						{addDollarSign(getNumBackInUnits(positionMargin, 18, 2))}
					</div>
				</div>
				<div class="flex justify-between items-center">
					<div class="text-sm text-fx-grey-400">TP/SL Fees:</div>
					<div class="text-sm text-fx-grey-300">
						{addDollarSign(getNumBackInUnits($tpslFees, 18, 2))}
					</div>
				</div>
			</div>
		</GradientBorder>

		<div
			transition:scale={{
				duration: 100,
				delay: 0,
				opacity: 0.9,
				start: 0.97,
				easing: quadInOut
			}}
		>
			<div class="border-t border-t-fx-grey-800 w-full mb-3 mt-4" />

			<TakeProfit />

			<StopLoss />
		</div>
		<div class="border-t border-t-fx-grey-800 w-full mt-4" />

		<div>
			{#if $loadingTPSL}
				<ProcessingButton title="Placing TP/SL Order" />
			{:else if !$isTakeProfitInputValid || !$isStopLossInputValid}
				<DisabledButton title="Confirm TP/SL" />
			{:else}
				<button
					class="w-full py-3 sm:py-4 text-fx-black bg-fx-gradient-23 rounded-xl squircle-xl font-semibold sm:text-sm text-xs"
					on:click={submitTPSL}
				>
					Confirm TP/SL
				</button>
			{/if}
		</div>
	</div>
</GradientBorder>
